import React, { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getProjectApprovals } from "../../../api/projectApprovalApi";
import { getAgreementApprovalsByApprover } from "../../../api/agreementsApi";
import { UserContext } from "../../../contexts/CurrentUserContext";
import Preloader from "../../Preloader/Preloader";
import AgreementApprovalList from "./AgreementApprovalList";
import { normalizeMyAgreementApprovalEntry } from "./agreementApprovalHelpers";
import "./ProjectApproval.css";

const TAB_LABELS = {
  estimate: "сметы",
  cp: "КП",
  plan: "плана",
  bonuses: "бонусов",
  agreements: "соглашений",
};

function ProjectApproval() {
  const navigate = useNavigate();
  const { userData } = useContext(UserContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState(tabFromUrl || "estimate");
  const [approvals, setApprovals] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProject, setSelectedProject] = useState("all");
  const [selectedInitiator, setSelectedInitiator] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [sortOrder, setSortOrder] = useState("desc");
  const [agreementPendingCount, setAgreementPendingCount] = useState(0);

  useEffect(() => {
    if (tabFromUrl && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
  }, [tabFromUrl, activeTab]);

  useEffect(() => {
    if (activeTab !== "estimate") {
      setIsLoading(false);
      return;
    }
    fetchApprovals();
  }, [activeTab]);

  const fetchApprovals = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await getProjectApprovals();
      setApprovals(data);
    } catch (err) {
      setError("Ошибка при загрузке проектов для согласования");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAgreementPendingCount = useCallback(async () => {
    if (!userData?.id) {
      setAgreementPendingCount(0);
      return;
    }
    try {
      const data = await getAgreementApprovalsByApprover(userData.id);
      const list = (Array.isArray(data) ? data : [])
        .map((entry) => normalizeMyAgreementApprovalEntry(entry, userData.id))
        .filter(Boolean);
      const pendingCount = list.filter((item) => {
        const myStatus = item.myStep?.status ?? item.status;
        return myStatus === "pending";
      }).length;
      setAgreementPendingCount(pendingCount);
    } catch {
      setAgreementPendingCount(0);
    }
  }, [userData?.id]);

  useEffect(() => {
    fetchAgreementPendingCount();
  }, [fetchAgreementPendingCount]);

  useEffect(() => {
    if (activeTab === "agreements") {
      fetchAgreementPendingCount();
    }
  }, [activeTab, fetchAgreementPendingCount]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === "estimate") {
      setSearchParams({});
    } else {
      setSearchParams({ tab });
    }
  };

  const formatCurrency = (value) => {
    if (!value) return "0";
    return new Intl.NumberFormat("ru-RU").format(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const getStatusIcon = (status) => {
    if (status === "Согласован" || status === "approved") {
      return <span className="project-approval__status-icon approved">✓</span>;
    }
    if (status === "На рассмотрении" || status === "pending") {
      return <span className="project-approval__status-icon pending">⏳</span>;
    }
    if (status === "Отклонен" || status === "rejected") {
      return <span className="project-approval__status-icon rejected">✕</span>;
    }
    return null;
  };

  const uniqueProjects = Array.from(
    new Set(approvals.map((a) => a.project_name)),
  ).filter(Boolean);

  const uniqueInitiators = Array.from(
    new Set(approvals.map((a) => a.initiator_name)),
  ).filter(Boolean);

  const filteredApprovals = approvals.filter((approval) => {
    if (
      selectedProject !== "all" &&
      approval.project_name !== selectedProject
    ) {
      return false;
    }
    if (
      selectedInitiator !== "all" &&
      approval.initiator_name !== selectedInitiator
    ) {
      return false;
    }
    return true;
  });

  const sortedApprovals = [...filteredApprovals].sort((a, b) => {
    const dateA = new Date(a.date);
    const dateB = new Date(b.date);
    return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
  });

  const renderPlaceholder = () => (
    <div className="project-approval__empty">
      Раздел «{TAB_LABELS[activeTab]}» в разработке
    </div>
  );

  const renderEstimateTab = () => {
    if (isLoading) return <Preloader />;
    if (error) {
      return <div className="project-approval__error">{error}</div>;
    }

    return (
      <>
        <div className="project-approval__filters">
          <div className="project-approval__filter">
            <label className="project-approval__filter-label">
              Выбор проекта
            </label>
            <select
              className="project-approval__filter-select"
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
            >
              <option value="all">Все проекты</option>
              {uniqueProjects.map((project) => (
                <option key={project} value={project}>
                  {project}
                </option>
              ))}
            </select>
          </div>

          <div className="project-approval__filter">
            <label className="project-approval__filter-label">Инициатор</label>
            <select
              className="project-approval__filter-select"
              value={selectedInitiator}
              onChange={(e) => setSelectedInitiator(e.target.value)}
            >
              <option value="all">Все</option>
              {uniqueInitiators.map((initiator) => (
                <option key={initiator} value={initiator}>
                  {initiator}
                </option>
              ))}
            </select>
          </div>

          <div className="project-approval__filter">
            <label className="project-approval__filter-label">Статус</label>
            <select
              className="project-approval__filter-select"
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
            >
              <option value="all">Все</option>
              <option value="pending">На рассмотрении</option>
              <option value="approved">Согласован</option>
              <option value="rejected">Отклонен</option>
            </select>
          </div>
        </div>

        {sortedApprovals.length === 0 ? (
          <div className="project-approval__empty">
            Нет сметы для согласования
          </div>
        ) : (
          <div className="project-approval__table-container">
            <table className="project-approval__table">
              <thead>
                <tr>
                  <th>Проект</th>
                  <th>Инициатор</th>
                  <th>Сумма</th>
                  <th>Статус</th>
                  <th>
                    <div className="project-approval__sort-header">
                      Дата отправки
                      <button
                        type="button"
                        className="project-approval__sort-button"
                        onClick={() =>
                          setSortOrder(sortOrder === "asc" ? "desc" : "asc")
                        }
                      >
                        {sortOrder === "asc" ? "↑" : "↓"}
                      </button>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedApprovals.map((approval) => (
                  <tr
                    key={approval.id}
                    className="project-approval__table-row"
                    onClick={() =>
                      navigate(
                        `/approvals/project-approval/${approval.project_id}/${approval.id}`,
                      )
                    }
                  >
                    <td>{approval.project_name || "—"}</td>
                    <td>{approval.initiator_name || "—"}</td>
                    <td>
                      {approval.total_price
                        ? `₸ ${formatCurrency(approval.total_price)}`
                        : "—"}
                    </td>
                    <td>
                      <div className="project-approval__status-list">
                        {approval.steps && approval.steps.length > 0 ? (
                          approval.steps.map((step, index) => {
                            const statusText =
                              step.status === "approved"
                                ? "Согласован"
                                : step.status === "pending"
                                  ? "На рассмотрении"
                                  : step.status === "rejected"
                                    ? "Отклонен"
                                    : step.status || "На рассмотрении";

                            return (
                              <div
                                key={step.id || index}
                                className="project-approval__status-item"
                              >
                                <span className="project-approval__approver-name">
                                  {step.approver_name || "Не указан"}:
                                </span>
                                {getStatusIcon(step.status)}
                                <span className="project-approval__status-text">
                                  {statusText}
                                </span>
                              </div>
                            );
                          })
                        ) : (
                          <span>—</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="project-approval__date">
                        <span className="project-approval__date-icon">📅</span>
                        {formatDate(approval.date)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="project-approval">
      <div className="project-approval__tabs">
        <button
          type="button"
          className={`project-approval__tab ${
            activeTab === "estimate" ? "active" : ""
          }`}
          onClick={() => handleTabChange("estimate")}
        >
          Смета
        </button>
        <button
          type="button"
          className={`project-approval__tab ${activeTab === "cp" ? "active" : ""}`}
          onClick={() => handleTabChange("cp")}
        >
          КП
        </button>
        <button
          type="button"
          className={`project-approval__tab ${
            activeTab === "plan" ? "active" : ""
          }`}
          onClick={() => handleTabChange("plan")}
        >
          План
        </button>
        <button
          type="button"
          className={`project-approval__tab ${
            activeTab === "bonuses" ? "active" : ""
          }`}
          onClick={() => handleTabChange("bonuses")}
        >
          Бонусы
        </button>
        <button
          type="button"
          className={`project-approval__tab ${
            activeTab === "agreements" ? "active" : ""
          }`}
          onClick={() => handleTabChange("agreements")}
        >
          <span className="project-approval__tab-label">Соглашения</span>
          {agreementPendingCount > 0 && (
            <span className="project-approval__tab-badge">
              {agreementPendingCount}
            </span>
          )}
        </button>
      </div>

      {activeTab === "estimate" && renderEstimateTab()}
      {activeTab === "agreements" && <AgreementApprovalList />}
      {(activeTab === "cp" ||
        activeTab === "plan" ||
        activeTab === "bonuses") &&
        renderPlaceholder()}
    </div>
  );
}

export default ProjectApproval;
